import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery } from "@tanstack/solid-query";
import { Show, createEffect, createSignal } from "solid-js";
import { everClaimedTicket, fetchProfile, updateProfile } from "~/lib/api";

export function EditProfilePage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const eventId = () => params().eventId ?? "";

  const [optIn, setOptIn] = createSignal(false);
  const [visibility, setVisibility] = createSignal<"hidden" | "directory">("hidden");

  const profileQuery = createQuery(() => ({
    queryKey: ["profile", eventId()],
    queryFn: () => fetchProfile(eventId()),
    enabled: !!eventId(),
  }));

  createEffect(() => {
    const p = profileQuery.data?.data;
    if (!p) return;
    setOptIn(!!p.networking_opt_in);
    setVisibility(p.visibility);
  });

  const saveMutation = createMutation(() => ({
    mutationFn: () =>
      updateProfile({
        ticketing_event_id: eventId(),
        networking_opt_in: optIn(),
        visibility: visibility(),
      }),
    onSuccess: () => {
      navigate({ to: "/directory/$eventId", params: { eventId: eventId() } });
    },
  }));

  const claimed = () => everClaimedTicket(profileQuery.data);

  return (
    <section class="page">
      <div class="page-title-block" style={{ padding: "20px 20px 0" }}>
        <p class="status-chip">This event</p>
        <h1>Conference settings</h1>
        <p class="muted">
          Choose how you appear in the directory for this city. Your bio and photo are edited once
          for all events.
        </p>
      </div>

      <div class="card" style={{ margin: "16px 20px" }}>
        <button
          type="button"
          class="btn btn-outline"
          style={{ width: "100%", "margin-bottom": "16px" }}
          onClick={() =>
            navigate({
              to: "/me/edit",
              search: { fromEvent: eventId() },
            })
          }
        >
          Edit bio, photo & interests
        </button>

        <Show when={!claimed()}>
          <p class="muted">
            Claim a ticket to a DEB event before you can opt in to the directory.
          </p>
        </Show>

        <Show when={claimed()}>
          <label class="switch-row">
            <span>Show me in the directory for this event</span>
            <input
              type="checkbox"
              checked={optIn()}
              aria-label="Show me in the directory for this event"
              onChange={(e) => setOptIn(e.currentTarget.checked)}
            />
          </label>

          <label class="field">
            <span>Directory visibility</span>
            <select
              value={visibility()}
              disabled={!optIn()}
              onChange={(e) => setVisibility(e.currentTarget.value as "hidden" | "directory")}
            >
              <option value="hidden">Hidden</option>
              <option value="directory">Visible in directory</option>
            </select>
          </label>

          <Show when={saveMutation.isError}>
            <div class="error-box">{(saveMutation.error as Error).message}</div>
          </Show>

          <button
            type="button"
            class="btn btn-primary"
            style={{ "margin-top": "12px" }}
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? "Saving…" : "Save event settings"}
          </button>
        </Show>

        <button
          type="button"
          class="btn btn-ghost"
          style={{ "margin-top": "12px" }}
          onClick={() => navigate({ to: "/directory/$eventId", params: { eventId: eventId() } })}
        >
          Back
        </button>
      </div>
    </section>
  );
}